import { observable, computed } from 'mobx';

import { SerializedData } from 'bernie-core';
import { AnalyticsStore, Store } from 'bernie-plugin-mobx';

import { InputValidator } from 'stores/validators';
import { trackAnalytics } from 'utility';
import {
  FlightsActionType,
  FlightsSearchFormErrorType,
  FlightsShoppingPwaFlightSearchFormLoaded,
  FlightsShoppingPwaFlightSearchResultsFlightsSearch,
  HotelsDate
} from '__generated__/api';
import Dialogs = FlightsShoppingPwaFlightSearchResultsFlightsSearch.Dialogs;
import ForcedChoiceCrossSellDialog = FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsForcedChoiceCrossSellDialogInlineFragment;
import PossibleErrors = FlightsShoppingPwaFlightSearchFormLoaded.PossibleErrors;
import Actions = FlightsShoppingPwaFlightSearchResultsFlightsSearch.Actions;
import HotelsDateField = HotelsDate.Fragment;

export class DialogsStore extends Store {
  @observable public dialogs: Dialogs[]; //TODO: Refer to Graphql type
  @observable public forcedChoiceDisplayedCounter: number;
  @observable public forcedChoiceModalVisible = false;

  public dialogsStoreInitialState: SerializedData;

  @computed get isForcedChoiceEnabled(): boolean {
    const forcedChoiceModalDialog = this.getForcedChoiceModalDialog();

    return forcedChoiceModalDialog && this.forcedChoiceDisplayedCounter < forcedChoiceModalDialog.displayCounter;
  }

  public setDialogsStoreInitialState(): void {
    this.dialogsStoreInitialState = this.toJSON();
  }

  public resetForcedChoiceModelErrors = () => {
    const { checkInDate, checkOutDate } = this.getForcedChoiceModalDialog();
    checkInDate.currentError = null;

    if (checkOutDate) {
      checkOutDate.currentError = null;
    }
  };

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  public getForcedChoiceModalDialog = () =>
    this.dialogs &&
    (this.dialogs.find(
      dialog => dialog.__typename === 'FlightsForcedChoiceCrossSellDialog'
    ) as ForcedChoiceCrossSellDialog);

  public getInitialForcedChoiceModalDialog = () =>
    this.dialogsStoreInitialState &&
    this.dialogsStoreInitialState.dialogs &&
    (this.dialogsStoreInitialState.dialogs.find(
      dialog => dialog.__typename === 'FlightsForcedChoiceCrossSellDialog'
    ) as ForcedChoiceCrossSellDialog);

  public validateForcedChoiceModal(analyticsStore: AnalyticsStore): boolean {
    let valid = true;
    this.resetForcedChoiceModelErrors();
    const { checkInDate, checkOutDate } = this.getForcedChoiceModalDialog();
    const defaultCheckInDate =
      this.getInitialForcedChoiceModalDialog() && this.getInitialForcedChoiceModalDialog().checkInDate;

    valid = this.validateDate(checkInDate, defaultCheckInDate, checkOutDate, analyticsStore);
    valid = this.validateDate(checkOutDate, defaultCheckInDate, checkInDate, analyticsStore) && valid;

    return valid;
  }

  private validateDate(
    dateToValidate: HotelsDateField,
    defaultStartDate: HotelsDateField,
    otherSelectedDate: HotelsDateField,
    analyticsStore: AnalyticsStore
  ): boolean {
    let valid = true;

    dateToValidate.possibleErrors.every(error => {
      let passValidation = true;
      if (error.type === FlightsSearchFormErrorType.MAX_STAY_DURATION_ERROR) {
        passValidation = InputValidator.DATE.MAX_STAY_DURATION_ERROR(otherSelectedDate, dateToValidate);
      } else {
        const validator = InputValidator.DATE[error.type] as (
          dateA: HotelsDateField,
          dateB?: HotelsDateField
        ) => boolean;
        passValidation = validator ? validator(dateToValidate, defaultStartDate) : true;
      }
      if (!passValidation) {
        const errorMessage = this.getErrorMessage(defaultStartDate, error);
        dateToValidate.currentError = errorMessage;
        trackAnalytics(error.analytics, analyticsStore);
        valid = false;
      }

      return valid;
    });

    return valid;
  }

  private getErrorMessage = (defaultDate: HotelsDateField, error: PossibleErrors) => {
    if (error && defaultDate) {
      switch (error.type) {
        case FlightsSearchFormErrorType.DATE_NOT_IN_VALID_RANGE_TO_DEFAULT_DATE:
          return error.message.replace('{defaultCheckInDate}', defaultDate.date.formatted);
        default:
          return error.message;
      }
    }
  };

  public incrementForcedChoiceDisplayedCounter = () => {
    this.forcedChoiceDisplayedCounter++;
  };

  public resetForcedChoiceDisplayedCounter = () => {
    this.forcedChoiceDisplayedCounter = 0;
  };

  public toggleForcedChoiceModalVisible = (): void => {
    if (this.isForcedChoiceEnabled) {
      this.forcedChoiceModalVisible = !this.forcedChoiceModalVisible;
    } else {
      this.forcedChoiceModalVisible = false;
    }
  };

  public updateForcedChoiceModal = (fisUrl?: string, packageableUrl?: string): void => {
    const forcedChoiceModal = this.getForcedChoiceModalDialog();

    if (forcedChoiceModal) {
      forcedChoiceModal.actions.map((action: Actions) => {
        switch (action.type) {
          case FlightsActionType.GO_TO_PACKAGES:
            action.relativePackageableURI = { __typename: 'HttpURI', relativePath: packageableUrl, value: null };
          case FlightsActionType.GO_TO_DETAILS:
            action.relativeURI = { __typename: 'HttpURI', relativePath: fisUrl, value: null };
          default:
        }
      });

      this.toggleForcedChoiceModalVisible();
    }
  };
}

DialogsStore.DEFAULTS = {
  dialogs: [],
  forcedChoiceDisplayedCounter: 0
};
